import * as React from 'react'
import { connect } from 'react-redux'
import { ServerCapabilityNames, ServerCapabilityRequirement, ServerCapabilityStats } from '../domain/ServerCapabilities'
import { ApplicationState } from '../redux/ApplicationState'
import { fetchServerCapabilities } from '../redux/V2/ServerCapability/actions'
import {
    areServerCapabilitiesLoaded,
    getServerCapability,
    getServerCapabilityRequirements,
} from '../redux/V2/ServerCapability/ServerCapabilityState'
import SymfonyLink from './link'

interface StateProps {
    environment: string
    service: string
    capabilityStats: ServerCapabilityStats | null
    capabilityRequirements: ServerCapabilityRequirement[] | null
    capabilitiesLoaded: boolean
}
interface DispatchProps {
    fetchServerCapabilities: () => void
}
interface OwnProps {
    capability: ServerCapabilityNames
    checkForActivation?: boolean
    expanded?: boolean
    failOnMissingCapabilities?: boolean
    level?: 'warning' | 'danger' | 'info'
    noMargin?: boolean
    hideChildrenOnCapabilityFail?: boolean
    hideChildrenOnInactive?: boolean
    children?: React.ReactNode
}

type Props = StateProps & DispatchProps & OwnProps

interface State {
    expanded: boolean
    discarded: boolean
}

class ServerCapabilityComponent extends React.PureComponent<Props, State> {
    public static defaultProps: any = {
        expanded: false,
        checkForActivation: false,
        failOnMissingCapabilities: false,
        level: 'warning',
        noMargin: false,
        hideChildrenOnCapabilityFail: false,
        hideChildrenOnInactive: true,
    }

    constructor(props) {
        super(props)

        this.state = { expanded: props.expanded, discarded: false }
    }
    public componentDidMount = (): void => {
        this.props.fetchServerCapabilities()
    }

    public componentDidUpdate = (prevProps: Props): void => {
        if (prevProps.expanded !== this.props.expanded) {
            this.setState({ expanded: this.props.expanded || false })
        }
    }

    public render = () => {
        const {
            capabilitiesLoaded,
            capabilityStats,
            capabilityRequirements,
            checkForActivation,
            failOnMissingCapabilities,
            hideChildrenOnCapabilityFail,
            hideChildrenOnInactive,
        } = this.props

        const { discarded, expanded } = this.state

        const margins = this.props.noMargin ? { marginLeft: 0, marginBottom: 0, marginTop: 0 } : {}

        if (!capabilitiesLoaded || discarded) {
            return this.props.children
        }

        if (capabilityStats === null && !failOnMissingCapabilities) {
            return this.props.children
        }

        if (capabilityStats === null) {
            return (
                <div className={'alert alert-' + this.props.level} style={{ ...margins, clear: 'both' }}>
                    No data about feature availability found, please upgrade php-extension and Tideways Daemon and try again.
                </div>
            )
        }

        if (capabilityStats.supported > 0 && (capabilityStats.activated > 0 || !checkForActivation)) {
            return this.props.children
        }

        if (hideChildrenOnCapabilityFail) {
            return null
        }

        let message = (
            <p>
                There {capabilityStats.totalServers === 1 ? 'is' : 'are'} {capabilityStats.totalServers} active{' '}
                {capabilityStats.totalServers === 1 ? 'server' : 'servers'} sending monitoring data for this service + environment, but none
                of them currently supports this feature, see below for complete requirements.
            </p>
        )
        if (capabilityStats.supported > 0 && capabilityStats.activated === 0) {
            message = (
                <p>
                    At least one of your servers supports this feature, but you still need to activate it, see below for complete
                    requirements.
                </p>
            )
        }

        const featureInactive = capabilityStats.supported > 0

        const onClick = expanded
            ? {}
            : {
                  onClick: () => this.setState({ expanded: true }),
              }

        // yeah, tslint ran into issues with max-line-length and lines having `{' '}` add the line end
        // so I extracted the variable to silence tslint, by having less indentation
        const supportText = (
            <p>
                See <SymfonyLink route={'XhprofProfilerBundle.Server.list'}>Server Configuration</SymfonyLink> for details and read up on{' '}
                <a href="https://support.tideways.com/category/127-upgrading?utm_medium=app&utm_source=server-capability">update</a> and{' '}
                <a href="https://support.tideways.com/category/125-configuration?utm_medium=app&utm_source=server-capability">configure</a>{' '}
                Tideways to add support for this feature.
            </p>
        )

        return (
            <React.Fragment>
                <div className={'tw-max-width-768 alert alert-' + this.props.level} style={{ ...margins, clear: 'both' }} {...onClick}>
                    {expanded ? (
                        <React.Fragment>
                            <h4>
                                {featureInactive ? (
                                    <span>
                                        <i className="fa fa-warning" /> Feature Supported, but not activated on any server
                                    </span>
                                ) : (
                                    <span>
                                        <i className="fa fa-warning" /> Feature Not Supported With Current Configuration
                                    </span>
                                )}
                            </h4>
                            {message}
                            {capabilityRequirements && capabilityRequirements.length > 0 && (
                                <React.Fragment>
                                    {supportText}
                                    <h4 className="tw-mt-8">Requirements</h4>
                                    <ul className="list-unstyled">
                                        {capabilityRequirements.map((requirement, index: number) => (
                                            <li key={index}>
                                                <i
                                                    className={
                                                        requirement.checkPassed === 0 ? 'fa fa-times tw-mr-8' : 'fa fa-check tw-mr-8'
                                                    }
                                                />
                                                {requirement.description}
                                                {!requirement.projectWideCheck ? (
                                                    <span className="text-muted tw-ml-16">
                                                        {requirement.checkPassed}/{requirement.total} servers
                                                    </span>
                                                ) : null}
                                                {requirement.checkPassed === 0 && requirement.settingsLink !== null ? (
                                                    <SymfonyLink route={requirement.settingsLink}> Settings</SymfonyLink>
                                                ) : null}
                                                {requirement.checkPassed === 0 && !requirement.projectWideCheck ? (
                                                    <a
                                                        className="tw-ml-16"
                                                        href={
                                                            requirement.docLink +
                                                            '?utm_medium=app&utm_source=server-capability-check&utm_campaign=server-capability'
                                                        }
                                                    >
                                                        Documentation
                                                    </a>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="text-right tw-mt-12">
                                        Are these values out-of-date?{' '}
                                        <a onClick={() => this.setState({ discarded: true })}>ignore warning and continue to feature</a>
                                    </p>
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    ) : (
                        <span>
                            {featureInactive ? (
                                <span>
                                    <i className="fa fa-warning" /> Feature Supported, but not activated on any server
                                </span>
                            ) : (
                                <span>
                                    <i className="fa fa-warning" /> Feature Not Supported With Current Configuration
                                </span>
                            )}
                            <br />
                            <a onClick={() => this.setState({ expanded: true })}>See details and instructions on how to update</a>.
                        </span>
                    )}
                </div>
                {featureInactive && !hideChildrenOnInactive ? this.props.children : null}
            </React.Fragment>
        )
    }
}

export const ServerCapability = connect<StateProps, DispatchProps, OwnProps>(
    (globalState: ApplicationState, props: Props): StateProps => {
        const { environment, service } = globalState.tidewaysContext

        return {
            environment,
            service,
            capabilityStats: getServerCapability(globalState, props.capability, environment, service),
            capabilityRequirements: getServerCapabilityRequirements(globalState, props.capability),
            capabilitiesLoaded: areServerCapabilitiesLoaded(globalState),
        }
    },
    {
        fetchServerCapabilities,
    },
)(ServerCapabilityComponent)
